import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { server, user } from 'stardog';
import { heapAgent } from 'vet-bones/utils';

import {
  Connection,
  ConnectionFailureReason,
  ConnectionStatus,
  createInitialConnectionsState,
} from 'src/store/connections/types';
import { createStardogAsyncThunk } from 'src/utils/request/createStardogAsyncThunk';

export const connectionAsyncActions = {
  requestCurrentPermissions: createStardogAsyncThunk<{ username: string }>(
    'connections/requestCurrentPermissions',
    ({ connection, username }) => user.get(connection, username)
  ),
  requestCurrentUser: createStardogAsyncThunk(
    'connections/requestCurrentUser',
    ({ connection }) => user.whoAmI(connection)
  ),
  requestServerStatus: createStardogAsyncThunk<{
    connection: Connection;
    params?: Parameters<typeof server.status>[1];
  }>('connections/requestServerStatus', ({ connection, params }) =>
    server.status(connection, params)
  ),
  requestServerProperties: createStardogAsyncThunk<{
    connection: Connection;
    params?: Parameters<typeof server.properties>[1];
  }>('connections/requestServerProperties', ({ connection, params }) =>
    server.properties(connection, params)
  ),
  requestValidateConnection: createStardogAsyncThunk<{
    shouldNotNotifySuccess?: boolean;
  }>('connections/requestValidateConnection', ({ connection }) =>
    user.valid(connection)
  ),
};

export const connectionsSlice = createSlice({
  name: 'connections',
  initialState: createInitialConnectionsState,
  reducers: {
    connectToStardogFinished: (state) => {
      state.pendingConnectToStardog = false;
    },
    connectToStardogStarted: (state) => {
      state.pendingConnectToStardog = true;
    },
    disconnected: (state) => {
      heapAgent.resetEventProperties();
      state.failureReason = null;
      state.permissions = null;
      state.status = null;
    },
    signedOut: (
      state,
      action: PayloadAction<{
        connection: Connection;
        /**
         * Whether to show the `SignInDialog` or not.
         *
         * When false we just setup connection on state.
         */
        showDialog?: boolean;
      }>
    ) => {
      heapAgent.resetEventProperties();
      state.connection = { ...action.payload.connection };
      state.failureReason = null;
      state.permissions = null;
      state.status = ConnectionStatus.SIGNED_OUT;
    },
    updatedConnectionToken: (
      state,
      action: PayloadAction<{ token: string }>
    ) => {
      if (state.connection?.token) {
        state.connection.token = action.payload.token;
      }
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(
      connectionAsyncActions.requestServerStatus.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        const stardogVersion: string | null =
          action.payload.body?.['dbms.version']?.value ?? null;
        const voiceboxDbLimit: number =
          action.payload.body?.['dbms.license.voicebox.limit']?.value ?? 0;
        const stardogLicenseType =
          action.payload.body?.['dbms.license.type']?.value;
        heapAgent.resetEventProperties({
          stardogVersion: stardogVersion || undefined,
          stardogLicenseType,
        });
        state.stardogVersionByConnectionId[connection.id] = stardogVersion;
        state.voiceboxDbLimitByConnectionId[connection.id] = voiceboxDbLimit;
      }
    );
    addCase(
      connectionAsyncActions.requestServerProperties.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        const catalogDatabase: string | null =
          action.payload.body?.['catalog.database'];
        state.catalogDatabaseByConnectionId[connection.id] =
          catalogDatabase || 'catalog';
        const querylogDatabase: string | null =
          action.payload.body?.['dbms.query.log.database'];
        state.querylogDatabaseByConnectionId[connection.id] =
          querylogDatabase || 'querylog';
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.pending,
      (state, action) => {
        const { connection } = action.meta.arg;
        if (connection) {
          state.connection = { ...connection };
          state.failureReason = null;
          state.permissions = null;
          state.status = ConnectionStatus.PENDING;
        }
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.rejected,
      (state, action) => {
        const status = action.payload?.response?.status;
        state.failureReason =
          status === undefined ? ConnectionFailureReason.NO_RESPONSE : null;
        state.permissions = null;
        state.status = ConnectionStatus.FAILURE;
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        if (connection) {
          state.connection = { ...connection };
          state.permissions = null;
          state.status = ConnectionStatus.SUCCESS;
        }
      }
    );
    addCase(
      connectionAsyncActions.requestCurrentPermissions.fulfilled,
      (state, action) => {
        state.permissions = action.payload.body?.permissions || null;
      }
    );
    addMatcher(
      (action) =>
        [
          connectionAsyncActions.requestCurrentPermissions.pending.type,
          connectionAsyncActions.requestCurrentPermissions.rejected.type,
        ].includes(action.type),
      (state) => {
        state.permissions = null;
      }
    );
  },
});
